/**
 * Non-destructive account edit dialog.
 *
 * Edits: canonicalName, panel, status, notes. Rate changes go through
 * their own PIN-gated flow elsewhere — this dialog only touches metadata.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api';
import type { Account } from '../api';
import {
  Button,
  ErrorBanner,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';

export interface EditAccountDialogProps {
  account: Account;
  onClose: () => void;
  onSaved: (updated: Account) => void;
}

export function EditAccountDialog({ account, onClose, onSaved }: EditAccountDialogProps) {
  const qc = useQueryClient();
  const [canonicalName, setCanonicalName] = useState(account.canonicalName);
  const [panel, setPanel] = useState(account.panel ?? '');
  const [status, setStatus] = useState<Account['status']>(account.status);
  const [notes, setNotes] = useState(account.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const saveMut = useMutation({
    mutationFn: () =>
      api.patch<Account>(`/api/accounts/${account.id}`, {
        canonicalName: canonicalName.trim(),
        panel: panel.trim().length === 0 ? null : panel.trim(),
        status,
        notes: notes.trim().length === 0 ? null : notes.trim(),
      }),
    onSuccess: (updated) => {
      void qc.invalidateQueries();
      onSaved(updated);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!canonicalName.trim()) {
      setError('Canonical name is required.');
      return;
    }
    saveMut.mutate();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-stretch justify-center bg-black/60 p-0 sm:items-center sm:p-4">
      <div className="flex min-h-[40vh] w-full max-w-xl flex-col overflow-hidden rounded-md border border-border bg-surface shadow-card">
        <header className="flex items-center justify-between border-b border-border bg-surface-raised px-4 py-3">
          <h2 className="text-small font-semibold text-ink">Edit account</h2>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </header>
        <form className="flex-1 overflow-y-auto p-4" onSubmit={onSubmit}>
          <div className="space-y-3">
            <TextInput
              label="Canonical name"
              value={canonicalName}
              onChange={(e) => setCanonicalName(e.target.value)}
              hint="Used for matching alias strings on import. Renaming affects future entries only — old ledger rows keep the original name."
              name="canonical-name"
            />
            <TextInput
              label="Panel"
              value={panel}
              onChange={(e) => setPanel(e.target.value)}
              placeholder="e.g. SAURABJI-CLICK"
              hint="Free-form label used to group accounts from the same upline sheet."
            />
            <Select
              label="Status"
              value={status}
              onChange={(v) => setStatus(v as Account['status'])}
              options={[
                { value: 'active', label: 'active' },
                { value: 'inactive', label: 'inactive' },
                { value: 'closed', label: 'closed' },
              ]}
            />
            <TextArea
              label="Notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              hint="Metadata only — won't affect any amount."
              placeholder="e.g. High turnover account; reconcile every Thursday"
            />
          </div>

          {error && (
            <div className="mt-3">
              <ErrorBanner>{error}</ErrorBanner>
            </div>
          )}

          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={saveMut.isPending}>
              {saveMut.isPending ? <Spinner /> : 'Save'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
